import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Mail, MapPin, Clock, MessageCircle, Send } from "lucide-react";
import { useAuthState } from "@/hooks/useAuthState";
import { ContactService } from "@/services/contactService";
import InputMask from "react-input-mask";
import { CountrySelect, Country } from "@/components/CountrySelect";

const FaleConosco = () => {
  const { isAuthenticated, userType, userName, userPhoto, userId } = useAuthState();
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [phoneDdi, setPhoneDdi] = useState("55"); // Brasil como padrão
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState<string>("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      
      // Validar e-mail antes de enviar
      if (!validateEmail(formData.email)) {
        toast({
          title: "Erro de validação",
          description: "Por favor, insira um e-mail válido",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }
      
      await ContactService.sendContactMessage(formData);
      
      toast({
        title: "Mensagem enviada!",
        description: "Entraremos em contato em breve. Obrigado!",
      });
      
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast({
        title: "Erro",
        description: "Não foi possível enviar sua mensagem. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Validar email em tempo real
    if (name === "email") {
      validateEmail(value);
    }
  };

  const validateEmail = (email: string) => {
    if (!email || email.trim() === "") {
      setEmailError("");
      return true;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Por favor, insira um e-mail válido");
      return false;
    }
    
    setEmailError("");
    return true;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar 
        isAuthenticated={isAuthenticated} 
        isOwner={userType === "owner"} 
        userName={userName}
        userPhoto={userPhoto}
        userId={userId}
        userType={userType}
      />

      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-primary/10 to-secondary/10">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">Fale Conosco</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Estamos aqui para ajudar você a planejar sua estadia perfeita no Porto 2 Life Resort
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 container mx-auto px-4">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          <Card className="text-center border shadow-soft hover:shadow-medium transition-all">
            <CardContent className="pt-8 pb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary/10 flex items-center justify-center">
                <Mail className="h-8 w-8 text-secondary" />
              </div>
              <h3 className="font-semibold mb-2">E-mail</h3>
              <a href="mailto:livestaybrasil@gmail.com" className="text-sm text-muted-foreground hover:text-foreground transition-colors block mb-1">
                livestaybrasil@gmail.com
              </a>
              <p className="text-xs text-muted-foreground">Resposta em até 24h</p>
            </CardContent>
          </Card>

          <Card className="text-center border shadow-soft hover:shadow-medium transition-all">
            <CardContent className="pt-8 pb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
                <MapPin className="h-8 w-8 text-accent" />
              </div>
              <h3 className="font-semibold mb-2">Endereço</h3>
              <p className="text-sm text-muted-foreground mb-1">Porto 2 Life Resort</p>
              <p className="text-xs text-muted-foreground">Muro Alto, Porto de Galinhas, PE</p>
            </CardContent>
          </Card>

          <Card className="text-center border shadow-soft hover:shadow-medium transition-all">
            <CardContent className="pt-8 pb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Clock className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Horário</h3>
              <p className="text-sm text-muted-foreground mb-1">Seg a Sex: 9h às 18h</p>
              <p className="text-xs text-muted-foreground">Sáb: 9h às 13h</p>
            </CardContent>
          </Card>
        </div>

        {/* Contact Form and Info */}
        <div className="grid lg:grid-cols-3 gap-12 max-w-7xl mx-auto">
          {/* Form */}
          <div className="lg:col-span-2">
            <Card className="border shadow-medium">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <MessageCircle className="h-6 w-6 text-primary" />
                  Envie sua Mensagem
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nome Completo *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Seu nome"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefone *</Label>
                      <div className="flex gap-2">
                        <CountrySelect
                          value={phoneDdi}
                          onValueChange={(ddi: string, country: Country) => {
                            setPhoneDdi(ddi);
                          }}
                          disabled={isSubmitting}
                        />
                        <InputMask
                          mask="(99) 99999-9999"
                          value={formData.phone}
                          onChange={(e) => {
                            const target = e.target as HTMLInputElement;
                            setFormData({
                              ...formData,
                              phone: target.value,
                            });
                          }}
                          disabled={isSubmitting}
                          className="flex-1"
                        >
                          {(inputProps: any) => (
                            <Input
                              {...inputProps}
                              id="phone"
                              name="phone"
                              type="tel"
                              placeholder="(00) 00000-0000"
                              required
                              disabled={isSubmitting}
                              className="flex-1"
                            />
                          )}
                        </InputMask>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">E-mail *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="seu@email.com"
                      className={emailError ? "border-destructive" : ""}
                      required
                    />
                    {emailError && (
                      <p className="text-sm text-destructive mt-1">{emailError}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">Assunto *</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="Sobre o que você gostaria de falar?"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Mensagem *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Conte-nos mais sobre sua dúvida ou solicitação..."
                      className="min-h-[150px]"
                      required
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full md:w-auto gap-2" 
                    size="lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Enviar Mensagem
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Additional Info */}
          <div className="space-y-6">
            <Card className="border shadow-soft">
              <CardHeader>
                <CardTitle className="text-lg">Reservas e Disponibilidade</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Para consultar disponibilidade e fazer reservas, você pode:
                </p>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>• Usar nossa busca online no site</li>
                  <li>• Enviar e-mail com suas datas</li>
                </ul>
                <Button className="w-full" variant="outline" asChild>
                  <a href="/busca?empreendimento=Porto 2 Life">
                    Ver Disponibilidade
                  </a>
                </Button>
              </CardContent>
            </Card>

            <Card className="border shadow-soft">
              <CardHeader>
                <CardTitle className="text-lg">Dúvidas Frequentes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Antes de entrar em contato, visite nossa Central de Ajuda. 
                  Lá você encontra respostas para as perguntas mais comuns.
                </p>
                <Button className="w-full" variant="outline" asChild>
                  <a href="/ajuda">
                    Central de Ajuda
                  </a>
                </Button>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-center">Nossa Localização</h2>
          <div className="max-w-5xl mx-auto">
            <Card className="border-0 shadow-large overflow-hidden">
              <div className="aspect-video bg-muted flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="h-16 w-16 text-primary mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Porto 2 Life Resort</h3>
                  <p className="text-muted-foreground">
                    Praia de Muro Alto, Porto de Galinhas - PE
                  </p>
                </div>
              </div>
            </Card>
            <div className="mt-6 text-center text-sm text-muted-foreground">
              <p>A 50 km do Aeroporto Internacional do Recife</p>
              <p>A 5 km do centro de Porto de Galinhas</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FaleConosco;